import type { InputSource, PatternsStatus } from "../../shared/patterns.js";
import type { Stack } from "../../shared/stack.js";
import { h } from "../dom.js";
import { ago, amount, full, stackDetail, stackName } from "../format.js";
import { stackIcon } from "./StackIcon.js";

const SOURCE_TITLE: Record<InputSource, string> = {
  craftable: "Autocraftable",
  autoprod: "Auto-prod: made by an always-running setup",
  missing: "No pattern makes it",
  any: "Any variant (wildcard in a crafting pattern)",
};

/** A stack with its amount, coloured by where it comes from when `source` is given. */
export function stackChip(stack: Stack, n: number | null, source?: InputSource): HTMLElement {
  const title = `${stackDetail(stack)}${n !== null ? `\n${full(n)}${stack.type === "fluid" ? " mB" : ""}` : ""}${source ? `\n${SOURCE_TITLE[source]}` : ""}`;
  return h(
    "span",
    { class: `chip${source ? ` src-${source}` : ""}`, title },
    stackIcon(stack),
    n !== null ? h("span", { class: "chip-qty" }, amount(n, stack.type)) : null,
    stackName(stack),
  );
}

/** "N patterns from M interfaces, loaded 5m ago", a progress line while loading, or the last error. */
export function patternsSummary(status: PatternsStatus | null, connected: boolean): HTMLElement {
  if (!status) return h("p", { class: "muted" }, "Loading…");
  const parts: Array<HTMLElement | string> = [];
  if (status.loading) {
    const of = status.loading.interfaces ? ` from ${status.loading.interfaces} interfaces` : "";
    parts.push(h("span", { class: "badge crafting" }, "Loading"), ` ${status.loading.received} patterns received${of}…`);
  } else if (status.at) {
    parts.push(`${full(status.count)} patterns from ${status.interfaces} interfaces, loaded ${ago(status.at)}.`);
  } else {
    parts.push(connected ? "Patterns have not been loaded yet." : "Patterns have not been loaded yet, and the game is not connected.");
  }
  return h(
    "div",
    null,
    h("p", null, ...parts),
    status.error && !status.loading
      ? h(
          "p",
          { class: "small warn" },
          `Last load failed: ${status.error}. `,
          /me_interface_terminal/.test(status.error)
            ? "Patterns are read through an Adapter placed against a cable that carries an ME Interface Terminal; add one next to the Butler's computer, then load again. " +
              "This needs GTNH 2.9 (OpenComputers 1.12.31-GTNH or newer): GTNH 2.8 cannot read patterns."
            : null,
        )
      : null,
  );
}
